import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  Typography,
} from "@mui/material";
import { CheckCircle, Error as ErrorIcon } from "@mui/icons-material";

/**
 * Reusable ImportPreview component for displaying data preview with validation
 * 
 * @param {Array} data - Preview data array
 * @param {Array} columns - Column definitions: [{ field, headerName, width? }]
 * @param {Function} getRowStatus - Function to get row status: (row) => 'valid' | 'error'
 * @param {Function} getRowError - Function to get row error message: (row) => string
 */
const ImportPreview = ({
  data = [],
  columns = [],
  getRowStatus,
  getRowError,
}) => {
  const getStatusIcon = (status) => {
    if (status === "valid") {
      return <CheckCircle sx={{ color: "success.main", fontSize: 18 }} />;
    }
    return <ErrorIcon sx={{ color: "error.main", fontSize: 18 }} />;
  };

  return (
    <Box>
      <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6" fontWeight={600}>
          Preview Data ({data.length} rows)
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Chip
            icon={<CheckCircle />}
            label={`${data.filter((row) => getRowStatus?.(row) === "valid").length} Valid`}
            color="success"
            size="small"
          />
          <Chip
            icon={<ErrorIcon />}
            label={`${data.filter((row) => getRowStatus?.(row) === "error").length} Errors`}
            color="error"
            size="small"
          />
        </Box>
      </Box>
      <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 50 }}>Status</TableCell>
              {columns.map((col) => (
                <TableCell key={col.field} sx={{ width: col.width, fontWeight: 600 }}>
                  {col.headerName}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, index) => {
              const status = getRowStatus?.(row) || "valid";
              const error = getRowError?.(row);
              return (
                <TableRow
                  key={index}
                  sx={{
                    bgcolor: status === "error" ? "error.light" : "transparent",
                    opacity: status === "error" ? 0.7 : 1,
                  }}
                >
                  <TableCell>{getStatusIcon(status)}</TableCell>
                  {columns.map((col) => (
                    <TableCell key={col.field}>
                      {row[col.field] || "-"}
                    </TableCell>
                  ))}
                  {error && (
                    <TableCell colSpan={columns.length}>
                      <Typography variant="caption" color="error">
                        {error}
                      </Typography>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ImportPreview;

